import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { AlarmClock, Archive, ChevronsLeftRight, KanbanSquare, LayoutList, Pencil, Rows3, Star, Table2, Trash2 } from 'lucide-react'
import { ApiError } from '@/api/request'
import { boardsApi, type BoardCard, type BoardGroupBy, type BoardInput, type BoardTickets } from '@/api/boardsApi'
import { ticketsApi, type UpdateTicketInput } from '@/api/ticketsApi'
import { BoardDialog } from '@/components/board/BoardDialog'
import { CardFace, DraggableCard, type CardAction, type Density } from '@/components/board/BoardCardView'
import { BoardListView } from '@/components/board/BoardListView'
import { FlipGroup } from '@/components/board/FlipGroup'
import { BoardFilterBar, BulkBar } from '@/components/board/BoardToolbar'
import { TicketPanel } from '@/components/board/TicketPanel'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState, ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { ResolveDialog } from '@/components/ticket/ResolveDialog'
import { Button } from '@/components/ui/button'
import { boardTicketsKey, useBoard, useBoardFolders, useBoardMutation, useBoardTickets } from '@/hooks/useBoards'
import { useBoardActions } from '@/hooks/useBoardActions'
import { BoardMenu } from '@/components/board/BoardMenu'
import { wipState } from '@/lib/boardLook'
import { useAgents } from '@/hooks/useDirectory'
import { useNow } from '@/hooks/useNow'
import { useTicket } from '@/hooks/useTickets'
import { applyMove, buildColumns, dropOutcome, moveInput, UNASSIGNED, type BoardColumn, type MovePermissions } from '@/lib/boardColumns'
import { commonMoves, customersOf, filterCards, filtersFromParams, filtersToParams, isAtRisk, type QuickFilters } from '@/lib/boardFilters'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { queueOfBoardId, queueTableUrl, rememberQueueView } from '@/lib/queueView'
import { priorityLabel, statusLabel, statusPillClass } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/ToastProvider'

const GROUPS: BoardGroupBy[] = ['status', 'assignee', 'priority']
type View = 'board' | 'list'

/** A per-board UI preference kept in localStorage (view, density, collapsed columns). */
function useBoardPref<T>(boardId: string, name: string, initial: T): [T, (v: T) => void] {
  const key = `ss.board.${boardId}.${name}`
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw === null ? initial : (JSON.parse(raw) as T)
    } catch {
      return initial
    }
  })
  const set = useCallback(
    (v: T) => {
      setValue(v)
      try {
        localStorage.setItem(key, JSON.stringify(v))
      } catch {
        // Private mode / quota: the preference just isn't remembered.
      }
    },
    [key],
  )
  return [value, set]
}

/** /boards/:id — cards in columns or a list, quick filters, bulk actions, and a ticket side panel. */
export default function BoardPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const { user } = useAuth()
  const meId = user?.id ?? null
  const now = useNow(30_000)
  const [params, setParams] = useSearchParams()
  const board = useBoard(id)
  const tickets = useBoardTickets(id)
  const agents = useAgents()
  const can: MovePermissions = {
    update: usePermission(PERMISSIONS.ticketsUpdate),
    assign: usePermission(PERMISSIONS.ticketsAssign),
    resolve: usePermission(PERMISSIONS.ticketsResolve),
  }
  const [viewGroup, setViewGroup] = useState<BoardGroupBy | null>(null)
  const [view, setView] = useBoardPref<View>(id, 'view', 'board')
  const [density, setDensity] = useBoardPref<Density>(id, 'density', 'comfortable')
  const [collapsed, setCollapsed] = useBoardPref<string[]>(id, 'collapsed', [])
  const [active, setActive] = useState<BoardCard | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [lastPicked, setLastPicked] = useState<string | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [resolving, setResolving] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // The card last dropped by drag: FlipGroup fades it in instead of flying it from its old column.
  const dropped = useRef<string | null>(null)
  const save = useBoardMutation((input: BoardInput) => boardsApi.update({ ...input, id }))
  const remove = useBoardMutation(() => boardsApi.remove(id))
  const folders = useBoardFolders()
  const actions = useBoardActions(folders.data)
  // A small move threshold keeps a click on the card opening it.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor))

  const filters = useMemo(() => filtersFromParams(params), [params])
  const openKey = params.get('ticket')
  const setFilters = (f: QuickFilters) => setParams(filtersToParams(f, params), { replace: true })
  const openTicket = useCallback(
    (key: string) => {
      setParams((p) => {
        const next = new URLSearchParams(p)
        next.set('ticket', key)
        return next
      })
    },
    [setParams],
  )
  const closeTicket = () => {
    const next = new URLSearchParams(params)
    next.delete('ticket')
    setParams(next)
  }

  const agentList = useMemo(() => agents.data ?? [], [agents.data])
  const allCards = useMemo(() => tickets.data?.items ?? [], [tickets.data])
  const groupBy = viewGroup ?? board.data?.groupBy ?? 'status'
  const cards = useMemo(() => filterCards(allCards, filters, meId, now), [allCards, filters, meId, now])
  const columns = useMemo(
    () => buildColumns(cards, groupBy, agentList, user ? { id: user.id, name: user.name } : null),
    [cards, groupBy, agentList, user],
  )
  const customers = useMemo(() => customersOf(allCards), [allCards])
  const selectedCards = useMemo(() => allCards.filter((c) => selected.has(c.id)), [allCards, selected])

  /** One move, from a drop or a card menu: optimistic, reverted on refusal. */
  const move = useCallback(
    async (card: BoardCard, grouping: BoardGroupBy, columnId: string) => {
      const outcome = dropOutcome(card, grouping, columnId, can)
      if (outcome === 'same') return
      if (outcome === 'blocked') {
        notify({ title: t('boards.notAllowed'), tone: 'warning' })
        return
      }
      if (outcome === 'resolve') {
        setResolving(card.key)
        return
      }
      const key = boardTicketsKey(id)
      // A board fetch already in flight would land on top of the optimistic move and snap the card back.
      await queryClient.cancelQueries({ queryKey: key })
      const before = queryClient.getQueryData<BoardTickets>(key)
      if (before) queryClient.setQueryData<BoardTickets>(key, { ...before, items: applyMove(before.items, card.id, grouping, columnId, agentList) })
      const to =
        grouping === 'status'
          ? statusLabel(columnId)
          : grouping === 'priority'
            ? priorityLabel(columnId)
            : columnId === UNASSIGNED
              ? t('boards.unassigned')
              : (agentList.find((a) => a.id === columnId)?.name ?? '')
      try {
        const saved = await ticketsApi.update(card.id, card.version, moveInput(grouping, columnId))
        // Keep the new version on the card so a second change before the refetch is not a 409.
        queryClient.setQueryData<BoardTickets>(key, (cur) => cur && { ...cur, items: cur.items.map((c) => (c.id === card.id ? { ...c, version: saved.version } : c)) })
        notify({ title: t('boards.moved', { key: card.key, to }) })
      } catch (err) {
        if (before) queryClient.setQueryData(key, before)
        notify({ title: t('boards.moveFailed', { key: card.key, message: errorMessage(err) }), tone: 'error' })
      } finally {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
    [can.update, can.assign, can.resolve, id, queryClient, agentList, notify, t],
  )

  const onAction = useCallback(
    (card: BoardCard, action: CardAction) => void move(card, action.kind, action.kind === 'assignee' ? (action.value ?? UNASSIGNED) : action.value),
    [move],
  )

  /** Toggle one card; Shift extends from the last picked card in the visible order. */
  const onSelect = useCallback(
    (cardId: string, range: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev)
        if (range && lastPicked) {
          const order = columns.flatMap((c) => c.cards.map((x) => x.id))
          const [a, b] = [order.indexOf(lastPicked), order.indexOf(cardId)].sort((x, y) => x - y)
          if (a >= 0 && b >= 0) order.slice(a, b + 1).forEach((x) => next.add(x))
        } else if (next.has(cardId)) next.delete(cardId)
        else next.add(cardId)
        return next
      })
      setLastPicked(cardId)
    },
    [columns, lastPicked],
  )

  /** One action on every selected card: a separate audited PATCH each; failures are reported, not hidden. */
  const bulk = async (input: UpdateTicketInput) => {
    setBulkBusy(true)
    const failed: string[] = []
    // A card already in the target status is skipped, not re-saved.
    const targets = input.status ? selectedCards.filter((c) => c.status !== input.status) : selectedCards
    for (const c of targets) {
      try {
        await ticketsApi.update(c.id, c.version, input)
      } catch {
        failed.push(c.key)
      }
    }
    setBulkBusy(false)
    void queryClient.invalidateQueries({ queryKey: boardTicketsKey(id) })
    const done = targets.length - failed.length
    notify(
      failed.length
        ? { title: t('boards.bulk.partial', { done, failed: failed.join(', ') }), tone: 'warning' }
        : { title: t('boards.bulk.done', { count: done }) },
    )
    setSelected(new Set(failed.length ? selectedCards.filter((c) => failed.includes(c.key)).map((c) => c.id) : []))
  }

  if (board.isPending || tickets.isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  if (board.isError) {
    const gone = board.error instanceof ApiError && board.error.status === 404
    return (
      <Page>
        {gone ? <EmptyState icon={KanbanSquare} message={t('boards.empty')} /> : <ErrorState message={errorMessage(board.error)} onRetry={() => void board.refetch()} />}
      </Page>
    )
  }
  if (tickets.isError)
    return (
      <Page>
        <ErrorState message={errorMessage(tickets.error)} onRetry={() => void tickets.refetch()} />
      </Page>
    )

  const b = board.data
  // A built-in queue board (queue-all, …): cards with a switch back to the queue table.
  const systemQueue = b.system ? queueOfBoardId(b.id) : null
  const columnLabel = (col: BoardColumn) =>
    groupBy === 'status' ? statusLabel(col.id) : groupBy === 'priority' ? priorityLabel(col.id) : col.id === UNASSIGNED ? t('boards.unassigned') : (col.name ?? '')
  const changeGroup = (g: BoardGroupBy) => {
    // The owner saves the grouping on the board; a viewer only changes their own view.
    if (b.mine) save.mutate({ groupBy: g }, { onSuccess: () => setViewGroup(null) })
    setViewGroup(g)
  }
  const toggleCollapsed = (colId: string) => setCollapsed(collapsed.includes(colId) ? collapsed.filter((c) => c !== colId) : [...collapsed, colId])
  const onDragStart = (e: DragStartEvent) => setActive((e.active.data.current as { card: BoardCard } | undefined)?.card ?? null)
  const onDragEnd = (e: DragEndEvent) => {
    const card = (e.active.data.current as { card: BoardCard } | undefined)?.card
    setActive(null)
    if (card && e.over) {
      dropped.current = card.id
      void move(card, groupBy, String(e.over.id))
    }
  }
  const segment = (on: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-[5px] px-2.5 py-1.5 text-meta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      on ? 'bg-accent font-medium text-accent-foreground' : 'text-ink-muted hover:text-ink',
    )

  return (
    <Page>
      {/* Title, actions and quick filters stay put while a long board scrolls under them;
          the negative margins cover the page padding so no card shows above the bar. */}
      <div className="sticky top-0 z-20 -mx-10 -mt-8 flex flex-col gap-4 border-b border-line bg-background/95 px-10 pb-1 pt-8 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <PageHeading
          description={b.system ? t('boards.queueBoard') : b.mine ? t('boards.eyebrow') : t('boards.sharedBy', { name: b.owner.name })}
          title={b.name}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div role="radiogroup" aria-label={t('boards.groupBy')} className="inline-flex rounded-md border border-line-strong p-0.5">
                {GROUPS.map((g) => (
                  <button key={g} type="button" role="radio" aria-checked={groupBy === g} onClick={() => changeGroup(g)} className={segment(groupBy === g)}>
                    {t(`boards.groups.${g}`)}
                  </button>
                ))}
              </div>
              {systemQueue ? (
                <div role="radiogroup" aria-label={t('boards.view.label')} className="inline-flex rounded-md border border-line-strong p-0.5">
                  <button type="button" role="radio" aria-checked className={segment(true)}>
                    <KanbanSquare className="size-4" aria-hidden />
                    {t('queue.view.cards')}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={false}
                    onClick={() => {
                      rememberQueueView('table')
                      navigate(queueTableUrl(systemQueue))
                    }}
                    className={segment(false)}
                  >
                    <Table2 className="size-4" aria-hidden />
                    {t('queue.view.table')}
                  </button>
                </div>
              ) : (
              <div role="radiogroup" aria-label={t('boards.view.label')} className="inline-flex rounded-md border border-line-strong p-0.5">
                <button type="button" role="radio" aria-checked={view === 'board'} onClick={() => setView('board')} className={segment(view === 'board')}>
                  <KanbanSquare className="size-4" aria-hidden />
                  {t('boards.view.board')}
                </button>
                <button type="button" role="radio" aria-checked={view === 'list'} onClick={() => setView('list')} className={segment(view === 'list')}>
                  <LayoutList className="size-4" aria-hidden />
                  {t('boards.view.list')}
                </button>
              </div>
              )}
              {view === 'board' || systemQueue ? (
                <Button
                  variant="outline"
                  size="icon"
                  aria-pressed={density === 'compact'}
                  aria-label={t('boards.view.compact')}
                  title={t('boards.view.compact')}
                  onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
                >
                  <Rows3 aria-hidden />
                </Button>
              ) : null}
              {b.system ? null : (
                <Button
                  variant="outline"
                  size="icon"
                  aria-pressed={b.starred}
                  aria-label={b.starred ? t('boards.unstar', { name: b.name }) : t('boards.star', { name: b.name })}
                  title={b.starred ? t('boards.unstar', { name: b.name }) : t('boards.star', { name: b.name })}
                  onClick={() => actions.toggleStar(b)}
                >
                  <Star className={cn(b.starred && 'fill-current text-sla-warning')} aria-hidden />
                </Button>
              )}
              {b.mine ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Pencil aria-hidden />
                    {t('boards.edit')}
                  </Button>
                  <Button variant="outline" size="icon" aria-label={t('boards.delete')} title={t('boards.delete')} onClick={() => setDeleting(true)}>
                    <Trash2 aria-hidden />
                  </Button>
                </>
              ) : null}
              {b.system ? null : <BoardMenu board={b} folders={folders.data} actions={actions} className="h-9 w-9 border border-line-strong" />}
            </div>
          }
        />
        {b.archivedAt ? (
          <div role="status" className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line-strong bg-surface-subtle px-3 py-2 text-meta text-ink-body">
            <Archive className="size-4 text-ink-muted" aria-hidden />
            <span className="grow">{t('boards.archivedBanner')}</span>
            {b.mine ? (
              <Button size="sm" variant="outline" onClick={() => actions.setArchived(b, false)}>
                {t('boards.restore')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {b.description ? <p className="-mt-1 mb-3 max-w-3xl text-meta text-ink-body">{b.description}</p> : null}
        <BoardFilterBar filters={filters} customers={customers} shown={cards.length} total={allCards.length} onChange={setFilters} />
      </div>
      {!b.mine && !b.system && viewGroup ? <p className="mb-3 text-label text-ink-muted">{t('boards.readOnlyGrouping')}</p> : null}
      {tickets.data.truncated ? (
        <p role="status" className="mb-3 rounded-md bg-status-customer-bg px-3 py-2 text-label text-status-customer-fg">
          {t('boards.truncated', { shown: allCards.length, total: tickets.data.total })}
        </p>
      ) : null}

      {view === 'list' && !systemQueue ? (
        <FlipGroup watch={columns}>
          <BoardListView
            columns={columns}
            label={columnLabel}
            now={now}
            selected={selected}
            agents={agentList}
            meId={meId}
            canAssign={can.assign}
            onAction={onAction}
            onOpen={openTicket}
            onSelect={onSelect}
          />
        </FlipGroup>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
          <FlipGroup watch={columns} skip={dropped}>
            <div className="flex min-h-[60vh] gap-3 overflow-x-auto pb-24" aria-label={b.name}>
              {columns.map((col) => (
                <Column
                  key={col.id}
                  column={col}
                  label={columnLabel(col)}
                  tone={groupBy === 'status' ? statusPillClass(col.id) : null}
                  atRisk={col.cards.filter((c) => isAtRisk(c, now)).length}
                  limit={b.wipLimits?.[col.id]}
                  collapsed={collapsed.includes(col.id)}
                  onToggle={() => toggleCollapsed(col.id)}
                  outcome={active ? dropOutcome(active, groupBy, col.id, can) : null}
                >
                  {col.cards.map((c) => (
                    <DraggableCard
                      key={c.id}
                      card={c}
                      now={now}
                      agents={agentList}
                      meId={meId}
                      can={can}
                      density={density}
                      selected={selected.has(c.id)}
                      onAction={onAction}
                      onOpen={openTicket}
                      onSelect={onSelect}
                    />
                  ))}
                </Column>
              ))}
            </div>
          </FlipGroup>
          <DragOverlay dropAnimation={null}>{active ? <CardFace card={active} now={now} density={density} dragging /> : null}</DragOverlay>
        </DndContext>
      )}

      {selected.size > 0 ? (
        <BulkBar
          count={selected.size}
          moves={commonMoves(selectedCards)}
          agents={agentList}
          meId={meId}
          can={{ update: can.update, assign: can.assign }}
          busy={bulkBusy}
          onMove={(status) => void bulk({ status })}
          onPriority={(priority) => void bulk({ priority })}
          onAssign={(assigneeId) => void bulk({ assigneeId })}
          onClear={() => setSelected(new Set())}
        />
      ) : null}

      <TicketPanel ticketKey={openKey} onClose={closeTicket} />
      {resolving ? <ResolveFromBoard ticketKey={resolving} onClose={() => setResolving(null)} /> : null}
      <BoardDialog
        open={editing}
        board={b}
        pending={save.isPending}
        error={save.error}
        onOpenChange={(o) => {
          setEditing(o)
          if (!o) save.reset()
        }}
        onSubmit={(input) => save.mutate(input, { onSuccess: () => setEditing(false) })}
      />
      <ConfirmDialog
        open={deleting}
        title={t('boards.deleteTitle', { name: b.name })}
        body={t('boards.deleteBody')}
        confirmLabel={t('boards.delete')}
        destructive
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onConfirm={() => remove.mutate(undefined, { onSuccess: () => navigate('/boards') })}
        onOpenChange={setDeleting}
      />
    </Page>
  )
}

/**
 * A drop target with a status-coloured count, an at-risk badge and a collapse
 * toggle. While a card is dragged, columns it cannot go to are dimmed and say why.
 */
function Column({
  column,
  label,
  tone,
  atRisk,
  limit,
  collapsed,
  onToggle,
  outcome,
  children,
}: {
  column: BoardColumn
  label: string
  tone: string | null
  atRisk: number
  /** The column's work-in-progress limit, if set. */
  limit?: number
  collapsed: boolean
  onToggle: () => void
  outcome: ReturnType<typeof dropOutcome> | null
  children: ReactNode
}) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: column.id })
  const blocked = outcome === 'blocked'
  // A WIP limit shows as "n / limit": amber at the limit, rust over it. It warns; moves are never blocked.
  const wip = wipState(column.cards.length, limit)
  const count = (
    <span
      title={wip === 'over' ? t('boards.wipOver', { limit }) : wip === 'at' ? t('boards.wipAt', { limit }) : undefined}
      className={cn(
        'rounded-full px-2 font-mono text-label tabular-nums',
        wip === 'over'
          ? 'bg-status-escalated-bg font-semibold text-status-escalated-fg'
          : wip === 'at'
            ? 'bg-status-customer-bg text-status-customer-fg'
            : (tone ?? 'bg-surface-subtle text-ink-muted'),
      )}
    >
      {column.cards.length}
      {wip !== 'none' && !collapsed ? ` / ${limit}` : null}
    </span>
  )

  if (collapsed)
    return (
      <section
        ref={setNodeRef}
        aria-label={label}
        className={cn('flex w-11 shrink-0 flex-col items-center gap-3 rounded-lg border border-line bg-surface-page py-3', isOver && !blocked && 'border-primary bg-accent', blocked && 'opacity-50')}
      >
        <button type="button" onClick={onToggle} aria-label={t('boards.expand', { name: label })} title={t('boards.expand', { name: label })} className="rounded p-1 text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronsLeftRight className="size-4" aria-hidden />
        </button>
        {count}
        <span className="text-meta font-semibold text-ink [writing-mode:vertical-rl]">{label}</span>
      </section>
    )

  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-xl border border-line bg-surface-page transition-colors',
        wip === 'over' && 'border-status-escalated-fg/40',
        isOver && !blocked && 'border-primary bg-accent',
        blocked && 'opacity-50',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2.5">
        <h2 className="truncate text-meta font-semibold text-ink">{label}</h2>
        {count}
        {atRisk > 0 ? (
          <span title={t('boards.atRiskCount', { count: atRisk })} className="inline-flex items-center gap-1 rounded-full bg-status-escalated-bg px-1.5 text-label font-medium text-status-escalated-fg">
            <AlarmClock className="size-3" aria-hidden />
            {atRisk}
          </span>
        ) : null}
        <span className="grow" />
        {blocked ? (
          <span className="truncate text-label text-ink-muted">{t('boards.notAllowed')}</span>
        ) : (
          <button type="button" onClick={onToggle} aria-label={t('boards.collapse', { name: label })} title={t('boards.collapse', { name: label })} className="rounded p-1 text-ink-faint hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronsLeftRight className="size-3.5" aria-hidden />
          </button>
        )}
      </header>
      <ul className="flex grow flex-col gap-2 px-2 pb-2">
        {column.cards.length === 0 ? (
          <li className="list-none rounded-lg border border-dashed border-line-strong px-3 py-5 text-center text-label text-ink-faint">
            {outcome && !blocked ? t('boards.dropHere') : t('boards.cardsEmpty')}
          </li>
        ) : null}
        {children}
      </ul>
    </section>
  )
}

/** Resolving keeps its dialog: load the ticket, then open the same Resolve dialog as the ticket page. */
function ResolveFromBoard({ ticketKey, onClose }: { ticketKey: string; onClose: () => void }) {
  const ticket = useTicket(ticketKey)
  if (!ticket.data) return null
  return <ResolveDialog ticket={ticket.data} routeId={ticketKey} open onOpenChange={(o) => (o ? undefined : onClose())} />
}
